import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  Settings, Trash2, Copy, Repeat, Play, Clock, 
  ChevronDown, ChevronUp, ChevronRight, Palette, Footprints, Check, Pause, Move,
  RotateCw, Maximize2, Eye, Bone, Camera, Sun, Save, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SPEED_PRESETS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

const CLIP_COLORS = [
  { name: 'Purple', value: 'bg-purple-600' },
  { name: 'Emerald', value: 'bg-emerald-600' },
  { name: 'Amber', value: 'bg-amber-600' },
  { name: 'Blue', value: 'bg-blue-600' },
  { name: 'Rose', value: 'bg-rose-600' },
  { name: 'Cyan', value: 'bg-cyan-600' },
];

// Track settings component for displaying when a track is selected
function TrackSettingsView({ 
  selectedTrack, 
  onTrackValueChange, 
  currentValues = {},
  currentFrame,
  recordMode,
  onAddKeyframe
}) {
  // Local state for immediate input response - keyed by track
  const [localValues, setLocalValues] = useState({});
  // Track which track we've initialized to avoid resetting on every render
  const [initializedTrack, setInitializedTrack] = useState(null);
  
  // Default values for each track type
  const getDefaultValue = (trackType, trackId) => {
    if (trackType === 'shot') {
      if (trackId === 'camera') {
        return { 
          position: { x: 0, y: 2, z: 5 }, 
          rotation: { x: -15, y: 0, z: 0 } 
        };
      }
      if (trackId === 'light') {
        return { 
          position: { x: 5, y: 10, z: 5 },
          intensity: 1
        };
      }
    }
    switch (trackId) {
      case 'position': return { x: 0, y: 0, z: 0 };
      case 'rotation': return { x: 0, y: 0, z: 0 };
      case 'scale': return { x: 1, y: 1, z: 1 };
      case 'visibility': return { visible: true, opacity: 1 };
      default: return {};
    }
  };
  
  // Get initial value from props
  const getInitialValue = () => {
    if (selectedTrack?.type === 'transform') {
      return currentValues.transforms?.[selectedTrack.id] || getDefaultValue('transform', selectedTrack.id);
    }
    if (selectedTrack?.type === 'yOffset') {
      return currentValues.yOffset || 0;
    }
    if (selectedTrack?.type === 'bone') {
      return currentValues.bones?.[selectedTrack.id] || { rotation: [0, 0, 0, 1] };
    }
    if (selectedTrack?.type === 'shot') {
      return currentValues.shot?.[selectedTrack.id] || getDefaultValue('shot', selectedTrack.id);
    }
    return null;
  };
  
  // Get track key for local state
  const trackKey = selectedTrack ? `${selectedTrack.type}-${selectedTrack.id || 'default'}` : 'none';
  
  // Only initialize local values when track selection changes (not on every render)
  React.useEffect(() => {
    if (selectedTrack && trackKey !== initializedTrack) {
      const initial = getInitialValue();
      setLocalValues(prev => ({
        ...prev,
        [trackKey]: initial
      }));
      setInitializedTrack(trackKey);
    }
  }, [trackKey, selectedTrack]);
  
  // Sync local values with external changes (e.g., mouse camera controls)
  React.useEffect(() => {
    if (selectedTrack?.type === 'shot' && currentValues?.shot) {
      const externalValue = currentValues.shot[selectedTrack.id];
      if (externalValue) {
        // Check if external value is different from local (to avoid infinite loops)
        const local = localValues[trackKey];
        const hasPositionDiff = 
          externalValue.position?.x !== local?.position?.x ||
          externalValue.position?.y !== local?.position?.y ||
          externalValue.position?.z !== local?.position?.z;
        const hasRotationDiff = 
          externalValue.rotation?.x !== local?.rotation?.x ||
          externalValue.rotation?.y !== local?.rotation?.y ||
          externalValue.rotation?.z !== local?.rotation?.z;
        
        if (hasPositionDiff || hasRotationDiff) {
          setLocalValues(prev => ({
            ...prev,
            [trackKey]: externalValue
          }));
        }
      }
    }
  }, [currentValues?.shot, selectedTrack?.type, selectedTrack?.id, trackKey]);
  
  // Reset initialized track when track deselected
  React.useEffect(() => {
    if (!selectedTrack) {
      setInitializedTrack(null);
    }
  }, [selectedTrack]);
  
  if (!selectedTrack) return null;
  
  // Get current value - prefer local state, fallback to props
  const trackValue = localValues[trackKey] !== undefined ? localValues[trackKey] : getInitialValue();
  
  const handleValueChange = (property, value) => {
    // Handle boolean values (like visible) vs numeric values
    let processedValue;
    if (typeof value === 'boolean') {
      processedValue = value;
    } else {
      const parsedValue = parseFloat(value);
      processedValue = isNaN(parsedValue) ? 0 : parsedValue;
    }
    
    const newValue = { ...trackValue, [property]: processedValue };
    
    // Update local state immediately for responsive UI
    setLocalValues(prev => ({
      ...prev,
      [trackKey]: newValue
    }));
    
    // Notify parent
    onTrackValueChange?.(selectedTrack, newValue);
    
    // If recording is enabled, automatically create keyframe
    if (recordMode === 'all' || recordMode === 'changes') {
      onAddKeyframe?.(selectedTrack, currentFrame, newValue, recordMode);
    }
  };
  
  const getTrackIcon = () => {
    switch (selectedTrack.type) {
      case 'transform':
        if (selectedTrack.id === 'position') return <Move className="w-4 h-4 text-blue-400" />;
        if (selectedTrack.id === 'rotation') return <RotateCw className="w-4 h-4 text-green-400" />;
        if (selectedTrack.id === 'scale') return <Maximize2 className="w-4 h-4 text-purple-400" />;
        if (selectedTrack.id === 'visibility') return <Eye className="w-4 h-4 text-cyan-400" />;
        break;
      case 'yOffset':
        return <Footprints className="w-4 h-4 text-amber-400" />;
      case 'bone':
        return <Bone className="w-4 h-4 text-slate-400" />;
      case 'shot':
        if (selectedTrack.id === 'camera') return <Camera className="w-4 h-4 text-amber-400" />;
        if (selectedTrack.id === 'light') return <Sun className="w-4 h-4 text-yellow-400" />;
        break;
      default:
        return <Settings className="w-4 h-4" />;
    }
  };
  
  const renderTrackControls = () => {
    switch (selectedTrack.type) {
      case 'transform':
        if (selectedTrack.id === 'position') {
          return (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Position X</label>
                <Input 
                  type="number" 
                  step="0.1" 
                  value={trackValue.x || 0}
                  onChange={(e) => handleValueChange('x', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Position Y</label>
                <Input 
                  type="number" 
                  step="0.1" 
                  value={trackValue.y || 0}
                  onChange={(e) => handleValueChange('y', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Position Z</label>
                <Input 
                  type="number" 
                  step="0.1" 
                  value={trackValue.z || 0}
                  onChange={(e) => handleValueChange('z', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
            </div>
          );
        }
        if (selectedTrack.id === 'rotation') {
          return (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Rotation X (deg)</label>
                <Input 
                  type="number" 
                  step="1" 
                  value={trackValue.x || 0}
                  onChange={(e) => handleValueChange('x', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Rotation Y (deg)</label>
                <Input 
                  type="number" 
                  step="1" 
                  value={trackValue.y || 0}
                  onChange={(e) => handleValueChange('y', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Rotation Z (deg)</label>
                <Input 
                  type="number" 
                  step="1" 
                  value={trackValue.z || 0}
                  onChange={(e) => handleValueChange('z', e.target.value)}
                  className="h-8 text-sm bg-slate-800 border-slate-600" 
                />
              </div>
            </div>
          );
        }
        if (selectedTrack.id === 'scale') {
          // Calculate uniform scale (average of x, y, z)
          const uniformScale = ((trackValue.x || 1) + (trackValue.y || 1) + (trackValue.z || 1)) / 3;
          
          return (
            <div className="space-y-3">
              {/* Main Uniform Scale Control */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Uniform Scale</label>
                <div className="flex items-center gap-2">
                  <Slider 
                    value={[uniformScale * 100]} 
                    min={10} 
                    max={300} 
                    step={5} 
                    className="flex-1"
                    onValueChange={(val) => {
                      const newScale = val[0] / 100;
                      // Update all three axes together
                      const newValue = { x: newScale, y: newScale, z: newScale };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newValue }));
                      onTrackValueChange?.(selectedTrack, newValue);
                    }}
                  />
                  <Input 
                    type="number" 
                    step="0.1" 
                    min="0.1"
                    value={uniformScale.toFixed(1)}
                    onChange={(e) => {
                      const newScale = parseFloat(e.target.value) || 1;
                      const newValue = { x: newScale, y: newScale, z: newScale };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newValue }));
                      onTrackValueChange?.(selectedTrack, newValue);
                    }}
                    className="w-16 h-8 text-sm bg-slate-800 border-slate-600" 
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Scales all axes uniformly</p>
              </div>
              
              {/* Collapsible Individual Axis Controls */}
              <details className="group">
                <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-300 flex items-center gap-1">
                  <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
                  Individual Axis Controls
                </summary>
                <div className="mt-2 space-y-2 pl-4 border-l border-slate-700">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Scale X</label>
                    <Input 
                      type="number" 
                      step="0.1" 
                      value={trackValue.x || 1}
                      onChange={(e) => handleValueChange('x', e.target.value)}
                      className="h-7 text-sm bg-slate-800 border-slate-600" 
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Scale Y</label>
                    <Input 
                      type="number" 
                      step="0.1" 
                      value={trackValue.y || 1}
                      onChange={(e) => handleValueChange('y', e.target.value)}
                      className="h-7 text-sm bg-slate-800 border-slate-600" 
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Scale Z</label>
                    <Input 
                      type="number" 
                      step="0.1" 
                      value={trackValue.z || 1}
                      onChange={(e) => handleValueChange('z', e.target.value)}
                      className="h-7 text-sm bg-slate-800 border-slate-600" 
                    />
                  </div>
                </div>
              </details>
            </div>
          );
        }
        if (selectedTrack.id === 'visibility') {
          return (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Visible</label>
                <div className="flex gap-2">
                  <Button 
                    variant={trackValue.visible !== false ? "secondary" : "ghost"} 
                    size="sm" 
                    className="flex-1 h-8"
                    onClick={() => handleValueChange('visible', true)}
                  >
                    On
                  </Button>
                  <Button 
                    variant={trackValue.visible === false ? "secondary" : "ghost"} 
                    size="sm" 
                    className="flex-1 h-8"
                    onClick={() => handleValueChange('visible', false)}
                  >
                    Off
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Opacity ({Math.round((trackValue.opacity ?? 1) * 100)}%)</label>
                <Slider 
                  value={[Math.round((trackValue.opacity ?? 1) * 100)]} 
                  max={100} 
                  step={1} 
                  className="mt-2"
                  onValueChange={(val) => handleValueChange('opacity', val[0] / 100)}
                />
              </div>
            </div>
          );
        }
        break;
      case 'yOffset':
        return (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Y Offset Value</label>
              <Input 
                type="number" 
                step="0.01" 
                value={typeof trackValue === 'number' ? trackValue : 0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  const newValue = isNaN(value) ? 0 : value;
                  // Update local state for responsive UI
                  setLocalValues(prev => ({
                    ...prev,
                    [trackKey]: newValue
                  }));
                  // Notify parent
                  onTrackValueChange?.(selectedTrack, newValue);
                }}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if (recordMode === 'all' || recordMode === 'changes') {
                    onAddKeyframe?.(selectedTrack, currentFrame, value, recordMode);
                  }
                }}
                className="h-8 text-sm bg-slate-800 border-slate-600" 
              />
            </div>
            <p className="text-xs text-slate-500">Adjusts character height to keep feet grounded</p>
          </div>
        );
      case 'bone':
        // Convert quaternion [x, y, z, w] to euler angles for display
        const quatToEuler = (q) => {
          if (!q || !Array.isArray(q)) return { x: 0, y: 0, z: 0 };
          const [qx, qy, qz, qw] = q;
          // Convert quaternion to euler (in degrees)
          const sinr_cosp = 2 * (qw * qx + qy * qz);
          const cosr_cosp = 1 - 2 * (qx * qx + qy * qy);
          const x = Math.atan2(sinr_cosp, cosr_cosp) * (180 / Math.PI);
          
          const sinp = 2 * (qw * qy - qz * qx);
          const y = Math.abs(sinp) >= 1 
            ? Math.sign(sinp) * 90 
            : Math.asin(sinp) * (180 / Math.PI);
          
          const siny_cosp = 2 * (qw * qz + qx * qy);
          const cosy_cosp = 1 - 2 * (qy * qy + qz * qz);
          const z = Math.atan2(siny_cosp, cosy_cosp) * (180 / Math.PI);
          
          return { x: Math.round(x), y: Math.round(y), z: Math.round(z) };
        };
        
        const euler = quatToEuler(trackValue?.rotation);
        
        return (
          <div className="space-y-3">
            <p className="text-xs text-slate-400 mb-2">Bone: {selectedTrack.name}</p>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Rotation X (deg)</label>
              <Input 
                type="number" 
                step="1" 
                value={euler.x}
                onChange={(e) => {
                  // This would need euler to quat conversion
                  console.log('Bone rotation X changed:', e.target.value);
                }}
                className="h-8 text-sm bg-slate-800 border-slate-600" 
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Rotation Y (deg)</label>
              <Input 
                type="number" 
                step="1" 
                value={euler.y}
                className="h-8 text-sm bg-slate-800 border-slate-600" 
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Rotation Z (deg)</label>
              <Input 
                type="number" 
                step="1" 
                value={euler.z}
                className="h-8 text-sm bg-slate-800 border-slate-600" 
              />
            </div>
          </div>
        );
      
      case 'shot':
        // Camera or Light track
        if (selectedTrack.id === 'camera') {
          return (
            <div className="space-y-3">
              <div className="text-xs text-slate-400 mb-2">Camera Position</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">X</label>
                  <Input 
                    type="number" 
                    step="0.5" 
                    value={trackValue?.position?.x ?? 0}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, x: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Y</label>
                  <Input 
                    type="number" 
                    step="0.5" 
                    value={trackValue?.position?.y ?? 2}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, y: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Z</label>
                  <Input 
                    type="number" 
                    step="0.5" 
                    value={trackValue?.position?.z ?? 5}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, z: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
              </div>
              <div className="text-xs text-slate-400 mb-2 mt-3">Camera Rotation (degrees)</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Pitch</label>
                  <Input 
                    type="number" 
                    step="5" 
                    value={trackValue?.rotation?.x ?? 0}
                    onChange={(e) => {
                      const newVal = { ...trackValue, rotation: { ...trackValue?.rotation, x: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Yaw</label>
                  <Input 
                    type="number" 
                    step="5" 
                    value={trackValue?.rotation?.y ?? 0}
                    onChange={(e) => {
                      const newVal = { ...trackValue, rotation: { ...trackValue?.rotation, y: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Roll</label>
                  <Input 
                    type="number" 
                    step="5" 
                    value={trackValue?.rotation?.z ?? 0}
                    onChange={(e) => {
                      const newVal = { ...trackValue, rotation: { ...trackValue?.rotation, z: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
              </div>
            </div>
          );
        }
        
        if (selectedTrack.id === 'light') {
          return (
            <div className="space-y-3">
              <div className="text-xs text-slate-400 mb-2">Light Position</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">X</label>
                  <Input 
                    type="number" 
                    step="1" 
                    value={trackValue?.position?.x ?? 5}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, x: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Y</label>
                  <Input 
                    type="number" 
                    step="1" 
                    value={trackValue?.position?.y ?? 10}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, y: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-0.5 block">Z</label>
                  <Input 
                    type="number" 
                    step="1" 
                    value={trackValue?.position?.z ?? 5}
                    onChange={(e) => {
                      const newVal = { ...trackValue, position: { ...trackValue?.position, z: parseFloat(e.target.value) || 0 } };
                      setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                      onTrackValueChange?.(selectedTrack, newVal);
                    }}
                    className="h-7 text-xs bg-slate-800 border-slate-600" 
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Intensity ({((trackValue?.intensity ?? 1) * 100).toFixed(0)}%)</label>
                <Slider
                  value={[(trackValue?.intensity ?? 1) * 100]}
                  onValueChange={([val]) => {
                    const newVal = { ...trackValue, intensity: val / 100 };
                    setLocalValues(prev => ({ ...prev, [trackKey]: newVal }));
                    onTrackValueChange?.(selectedTrack, newVal);
                  }}
                  min={0}
                  max={200}
                  step={10}
                />
              </div>
            </div>
          );
        }
        return null;
        
      default:
        return null;
    }
  };
  
  return (
    <div className="p-3 border-b border-slate-700 bg-slate-800/50">
      <div className="flex items-center gap-2 mb-3">
        {getTrackIcon()}
        <h4 className="text-sm font-medium text-white">{selectedTrack.name} Settings</h4>
        {recordMode && recordMode !== 'off' && (
          <span className={`text-xs px-2 py-0.5 rounded ${recordMode === 'all' ? 'bg-red-600' : 'bg-orange-600'}`}>
            Recording
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 mb-3">Frame: {currentFrame}</div>
      {renderTrackControls()}
      <Button
        size="sm"
        className="w-full mt-3 h-7 text-xs"
        onClick={() => onAddKeyframe?.(selectedTrack, currentFrame, trackValue, 'changes')}
      >
        Add Keyframe at Frame {currentFrame}
      </Button>
    </div>
  );
}

export default function ClipSettingsPanel({
  clip,
  onClipChange,
  onDeleteClip,
  onDuplicateClip,
  onSetRootMotionMode,
  rootMotionMode = 'normal',
  selectedTrack,
  onTrackValueChange,
  onAddKeyframe,
  currentFrame = 0,
  recordMode = 'off',
  currentTrackValues = {},
  className = '',
  onSaveAsSignature,
  userId,
  userEmail
}) {
  // Use clip values directly
  const localSpeed = clip?.speed || 1;
  const localLoops = clip?.loops || 1;
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // If a track is selected, show track settings first
  if (selectedTrack) {
    return (
      <div className={`flex flex-col h-full overflow-y-auto ${className}`}>
        <div className="p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Track Settings
          </h3>
        </div>
        <TrackSettingsView 
          selectedTrack={selectedTrack} 
          onTrackValueChange={onTrackValueChange}
          currentValues={currentTrackValues}
          currentFrame={currentFrame}
          recordMode={recordMode}
          onAddKeyframe={onAddKeyframe}
        />
        <div className="p-3 text-xs text-slate-500">
          Click elsewhere to deselect track and see clip settings
        </div>
      </div>
    );
  }

  if (!clip) {
    return (
      <div className={`flex flex-col h-full ${className}`}>
        <div className="p-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Clip Settings
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-slate-500">
            <Settings className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Select a clip to edit</p>
            <p className="text-xs mt-1 opacity-70">Click on a clip in the timeline</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSpeedChange = (newSpeed) => {
    // Calculate new duration based on speed
    // Faster speed = shorter duration
    const baseDuration = clip.animation?.total_frames || 48;
    const adjustedDuration = Math.round((baseDuration * localLoops) / newSpeed);
    
    onClipChange({
      ...clip,
      speed: newSpeed,
      duration: adjustedDuration
    });
  };

  const handleLoopsChange = (newLoops) => {
    const loops = Math.max(1, Math.min(10, newLoops));
    
    const baseDuration = clip.animation?.total_frames || 48;
    const adjustedDuration = Math.round((baseDuration * loops) / localSpeed);
    
    onClipChange({
      ...clip,
      loops,
      duration: adjustedDuration
    });
  };

  const handleColorChange = (newColor) => {
    onClipChange({ ...clip, color: newColor });
    setShowColorPicker(false);
  };

  const animationName = clip.animation?.name || 'Clip';
  const duration = clip.duration / 24; // frames to seconds at 24fps
  const effectiveDuration = duration * localLoops / localSpeed;
  
  // Root motion status
  const isBlankTemplate = clip.animation?.is_blank_template;

  return (
    <div className={`flex flex-col h-full overflow-y-auto ${className}`}>
      {/* Header */}
      <div className="p-3 border-b border-slate-700">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Clip Settings
        </h3>
      </div>

      {/* Clip Info */}
      <div className="p-3 border-b border-slate-700">
        <div className="flex items-center gap-2 mb-2">
          <Play className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-white">{animationName}</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span>Start: {(clip.startFrame / 24).toFixed(2)}s</span>
          <span>Duration: {effectiveDuration.toFixed(2)}s</span>
        </div>
      </div>

      {/* Speed Control */}
      <div className="p-3 border-b border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-slate-300 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Speed
          </label>
          <span className="text-xs text-purple-400 font-mono">{localSpeed}x</span>
        </div>
        
        {/* Speed slider */}
        <Slider
          value={[localSpeed]}
          onValueChange={([value]) => handleSpeedChange(value)}
          min={0.1}
          max={2}
          step={0.05}
          className="mb-2"
        />
        
        {/* Speed presets */}
        <div className="flex flex-wrap gap-1">
          {SPEED_PRESETS.map((preset) => (
            <Button
              key={preset}
              variant="ghost"
              size="sm"
              onClick={() => handleSpeedChange(preset)}
              className={`h-6 px-2 text-xs ${
                localSpeed === preset 
                  ? 'bg-purple-600/30 text-purple-300' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {preset}x
            </Button>
          ))}
        </div>
        
        <p className="text-xs text-slate-500 mt-2">
          Adjusts playback timing. Higher = faster animation.
        </p>
      </div>

      {/* Loops Control */}
      <div className="p-3 border-b border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-slate-300 flex items-center gap-1">
            <Repeat className="w-3 h-3" />
            Loops
          </label>
          <span className="text-xs text-slate-400">
            Base: {duration.toFixed(2)}s
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleLoopsChange(localLoops - 1)}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
            disabled={localLoops <= 1}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          
          <Input
            type="number"
            min={1}
            value={localLoops}
            onChange={(e) => handleLoopsChange(parseInt(e.target.value) || 1)}
            className="h-8 w-16 text-center text-sm bg-slate-800 border-slate-600"
          />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleLoopsChange(localLoops + 1)}
            className="h-8 w-8 p-0 text-slate-400 hover:text-white"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
        </div>
        
        <p className="text-xs text-slate-500 mt-2">
          Drag clip edge to loop, or adjust here.
        </p>
      </div>

      {/* Color Picker */}
      <div className="p-3 border-b border-slate-700">
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="flex items-center justify-between w-full text-xs font-medium text-slate-300 hover:text-white"
        >
          <span className="flex items-center gap-1">
            <Palette className="w-3 h-3" />
            Clip Color
          </span>
          <div className={`w-4 h-4 rounded ${clip.color || 'bg-purple-600'}`} />
        </button>
        
        {showColorPicker && (
          <div className="flex flex-wrap gap-2 mt-2">
            {CLIP_COLORS.map((color) => (
              <button
                key={color.value}
                onClick={() => handleColorChange(color.value)}
                className={`w-6 h-6 rounded ${color.value} hover:ring-2 hover:ring-white/50
                  ${clip.color === color.value ? 'ring-2 ring-white' : ''}`}
                title={color.name}
              />
            ))}
          </div>
        )}
      </div>

      {/* Root Motion Section - Shows status of automatic root motion */}
      {!isBlankTemplate && (
        <div className="p-3 border-b border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Footprints className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-medium text-white">Root Motion</span>
          </div>
          
          {/* Check if animation has root motion data */}
          {clip.animation?.rootMotionTrack?.enabled || clip.animation?.rootMotion?.enabled ? (
            <div className="space-y-2">
              {/* Mode Toggle */}
              <div className="flex gap-1 p-1 bg-slate-800 rounded" data-testid="root-motion-toggle">
                <button
                  onClick={() => onSetRootMotionMode && onSetRootMotionMode('normal')}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                    rootMotionMode !== 'treadmill'
                      ? 'bg-cyan-600 text-white'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  data-testid="locomotion-btn"
                >
                  <Move className="w-3 h-3" />
                  Locomotion
                </button>
                <button
                  onClick={() => onSetRootMotionMode && onSetRootMotionMode('treadmill')}
                  className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs transition-colors ${
                    rootMotionMode === 'treadmill'
                      ? 'bg-amber-600 text-white'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  data-testid="treadmill-btn"
                >
                  <Pause className="w-3 h-3" />
                  Treadmill
                </button>
              </div>
              
              {/* Status Message */}
              {rootMotionMode === 'treadmill' ? (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2">
                  <p className="text-[10px] text-amber-400">
                    Character animates in place without moving
                  </p>
                </div>
              ) : (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
                  <p className="text-[10px] text-green-400">
                    Character moves {(clip.animation?.rootMotionTrack?.totalDistance || clip.animation?.rootMotion?.distancePerCycle || 1.3).toFixed(2)}m per cycle
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-800/50 rounded p-2">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-300">Stationary animation</span>
              </div>
              <p className="text-[10px] text-slate-400/70 mt-1">
                Character stays in place during this animation
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-3 space-y-2">
        {/* Save as Signature button - only show if clip has content */}
        {(clip.animation?.keyframes?.length > 0 || clip.animation?.transformKeyframes?.length > 0) && (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300"
            onClick={() => {
              setSignatureName(clip.animation?.name ? `${clip.animation.name} (Custom)` : 'My Animation');
              setShowSaveModal(true);
            }}
            data-testid="save-as-signature-btn"
          >
            <Save className="w-3 h-3 mr-2" />
            Save as Signature
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs border-slate-600 hover:bg-slate-800"
          onClick={() => onDuplicateClip(clip)}
        >
          <Copy className="w-3 h-3 mr-2" />
          Duplicate Clip
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs border-red-500/50 text-red-400 hover:bg-red-500/20 hover:text-red-300"
          onClick={() => onDeleteClip(clip.id)}
        >
          <Trash2 className="w-3 h-3 mr-2" />
          Delete Clip
        </Button>
      </div>

      {/* Save as Signature Modal */}
      <Dialog open={showSaveModal} onOpenChange={setShowSaveModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-purple-400" />
              Save as Signature Clip
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Save this animation to your personal library. You can reuse it in any project.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Animation Name</label>
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Enter a name for your animation"
                className="bg-slate-800 border-slate-600 text-white"
                data-testid="signature-name-input"
              />
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-3 space-y-1 text-xs text-slate-400">
              <p><strong className="text-slate-300">Duration:</strong> {clip.duration} frames ({(clip.duration / 24).toFixed(2)}s)</p>
              <p><strong className="text-slate-300">Keyframes:</strong> {clip.animation?.keyframes?.length || 0} bone, {clip.animation?.transformKeyframes?.length || 0} transform</p>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowSaveModal(false)}
              className="border-slate-600"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!signatureName.trim()) {
                  toast.error('Please enter a name');
                  return;
                }
                
                setIsSaving(true);
                try {
                  // Prepare animation data
                  const animationData = {
                    name: signatureName.trim(),
                    description: `Custom animation saved from ${clip.animation?.name || 'clip'}`,
                    keyframes: clip.animation?.keyframes || [],
                    transform_keyframes: clip.animation?.transformKeyframes || [],
                    total_frames: clip.duration || 48,
                    fps: 24,
                    root_motion: clip.animation?.rootMotion || { enabled: true, distancePerCycle: 1.3 },
                    folder: 'signature'
                  };
                  
                  // Save via API - JSON body with query params for user info
                  const saveResponse = await axios.post(
                    `${API}/api/animations`,
                    animationData,
                    {
                      params: {
                        user_id: userId || 'anonymous',
                        user_email: userEmail || 'anonymous@user.com'
                      }
                    }
                  );
                  
                  toast.success(`Saved "${signatureName}" to your Signature clips!`);
                  setShowSaveModal(false);
                  
                  // Callback to refresh animation library
                  if (onSaveAsSignature) {
                    onSaveAsSignature(saveResponse.data.animation);
                  }
                } catch (error) {
                  console.error('Failed to save signature clip:', error);
                  toast.error(error.response?.data?.detail || 'Failed to save animation');
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving || !signatureName.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-save-signature-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tips */}
      <div className="mt-auto p-3 bg-slate-800/50 text-xs text-slate-500">
        <p className="font-medium text-slate-400 mb-1">Tips:</p>
        <ul className="space-y-0.5">
          <li>• Double-click clip to edit bones</li>
          <li>• Drag edge to loop animation</li>
          <li>• Speed affects export timing</li>
        </ul>
      </div>
    </div>
  );
}
